import { Input, Tree } from "antd";
import styled from "styled-components";

// Styled Components for OrganizationForecasts component
export const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

export const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
`;

export const HeaderText = styled.div`
  flex: 1;
`;

export const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: var(--color-text);
`;

export const Description = styled.p`
  font-size: 14px;
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.5;
`;

export const TreeHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

export const SearchContainer = styled.div`
  flex: 1;
  max-width: 400px;
`;

export const CreateFolderButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  background: white;
  color: #a2aaad;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  height: 32px;

  &:hover {
    border-color: var(--color-primary);
    color: var(--color-primary);
    background: rgba(var(--color-primary-rgb), 0.04);
  }

  svg {
    font-size: 18px;
  }
`;

export const TreeNodeTitle = styled.div`
  display: flex !important;
  align-items: center;
  justify-content: space-between;
  width: 100% !important;
  min-width: 100% !important;
  padding: 4px 8px;
  border-radius: 4px;

  &:hover {
    background-color: #f5f5f5;
  }
`;

export const TreeNodeContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;

  .title-text {
    font-size: 14px;
    line-height: 1.5;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .title-reservecategory {
    display: block;
    background-color: #dfdfdf;
    padding: 1px 3px;
    margin-left: 5px;
    border-radius: 8px;
    font-size: 10px;
    font-weight: bold;
  }

  svg {
    color: #a2aaad;
    font-size: 18px;
    flex-shrink: 0;
  }
`;

export const TreeContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  height: 100%;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  padding: 16px;
  background: white;

  .ant-tree-switcher {
    transition: all 0.2s;
  }
`;

export const EditInput = styled(Input)`
  && {
    font-size: 14px;
    line-height: 1.5;
    border: 1px solid var(--color-primary);
    border-radius: 4px;
    padding: 2px 8px;
    height: auto;
    min-height: 24px;

    &:focus {
      border-color: var(--color-primary);
      box-shadow: 0 0 0 2px rgba(var(--color-primary-rgb), 0.1);
    }
  }
`;

export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  gap: 16px;
`;

export const LoadingText = styled.div`
  font-size: 14px;
  color: var(--color-text-secondary);
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  gap: 8px;
`;

export const EmptyStateText = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: var(--color-text);
`;

export const EmptyStateSubtext = styled.div`
  font-size: 14px;
  color: var(--color-text-secondary);
  text-align: center;
`;

// Styled Tree component with all the custom Ant Design styles
export const StyledTree = styled(Tree)`
  background: transparent;

  .ant-tree-list-holder,
  .ant-tree-list,
  .ant-tree-treenode-leaf-last,
  .ant-tree-treenode {
    width: 100% !important;
  }

  // Tree node layout
  .ant-tree-treenode {
    display: flex !important;
    border-bottom: 4px solid transparent;
    margin-bottom: 4px !important;
    padding: 0;

    .ant-tree-node-content-wrapper {
      width: 100% !important;
      flex: 1 !important;
      display: flex !important;
      flex-direction: row;
      justify-content: flex-start;
      align-items: center;
      padding: 0 !important;
      border-radius: 4px;
      transition: all 0.2s;

      &:hover {
        background-color: transparent;
      }

      label {
        margin: 0;
      }
    }

    .ant-tree-iconEle {
      line-height: 26px;
    }

    .ant-tree-title {
      font-size: 14px;
      line-height: 1.5;
      width: 100% !important;
      flex: 1 !important;
    }
  }

  // Switcher styles
  .ant-tree-switcher {
    display: flex;
    align-items: center;
    justify-content: center;

    &-icon {
      font-size: 12px;
    }

    &:hover::after {
      border-color: var(--color-primary);
    }

    &.ant-tree-switcher_open {
      transform: rotate(90deg);
    }

    svg {
      display: none;
    }

    &-noop {
      opacity: 0.25;
      pointer-events: none;

      .shapefile-leaf & {
        opacity: 0;
      }
    }

    // Hide the default ::after chevron when a hidden switcher is present
    &:has(.hidden-switcher)::after {
      display: none !important;
    }

    // Style the switcher container when it has a hidden switcher with tooltip
    &:has(.hidden-switcher.with-tooltip) {
      cursor: not-allowed;
    }
  }

  // Org folders: hide the default noop switcher chevron entirely
  .org-folder-node .ant-tree-switcher-noop {
    opacity: 0;
    pointer-events: none;

    &::after {
      display: none;
    }
  }

  // Hidden switcher style - no visible element but maintains space for tooltip
  .hidden-switcher {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;

    &.with-tooltip {
      cursor: not-allowed;
    }
  }

  // Checkbox styles
  .ant-tree-checkbox {
    &-inner {
      width: 18px;
      height: 18px;
    }

    &:hover .ant-tree-checkbox-inner {
      border-color: var(--color-primary);
    }

    &-checked .ant-tree-checkbox-inner {
      background-color: var(--color-primary);
      border-color: var(--color-primary);
    }

    &-indeterminate .ant-tree-checkbox-inner::after {
      background-color: var(--color-primary);
      width: 6px;
      height: 6px;
    }
  }

  .ant-tree-node-content-wrapper.ant-tree-node-selected {
    background-color: inherit;
  }

  .ant-tree-draggable-icon {
    display: none !important;
  }

  .ant-tree-list-scrollbar .ant-tree-list-scrollbar-thumb {
    border-radius: 0 !important;
    background: var(--color-scrollbar-thumb) !important;
  }

  .ant-tree-indent-unit {
    width: 19px;
  }

  .ant-tree,
  .ant-tree-list {
    height: 100%;
    width: 100%;
  }

  .ant-tree-treenode[aria-expanded] .title-text {
    font-weight: 600;
  }

  .ant-tree-treenode:not([aria-expanded]) .title-text {
    font-weight: 400;
    color: var(--color-text-secondary);
  }

  .visible-on-hover svg {
    opacity: 0;
    transition: opacity 0.2s;
  }

  .ant-tree-treenode:hover .visible-on-hover svg {
    opacity: 1;
  }

  .tree-title.drag-drop {
    border: 2px dashed gray;
    padding: 0 5px;
  }
`;

// Styled Input component with custom styling
export const StyledInput = styled(Input)`
  && {
    border-radius: 6px;
    border-color: #d9d9d9;

    &:hover {
      border-color: #40a9ff;
    }

    &:focus-within {
      border-color: var(--color-primary);
      box-shadow: 0 0 0 2px rgba(var(--color-primary-rgb), 0.2);
    }

    .ant-input {
      font-size: 14px;
    }

    .ant-input-prefix {
      color: #a2aaad;
    }
  }
`;
